/**
 * Hook factories — eliminate boilerplate across 100+ hook files.
 *
 * Usage:
 *
 *   // Global resource (e.g. /borrowers)
 *   const { useList, useDetail, useCreate, useUpdate, useDelete } =
 *     createCrudHooks<Borrower, CreateDto, UpdateDto>('borrowers', '/borrowers');
 *
 *   // Loan-scoped resource (e.g. /loans/:loanId/securities)
 *   const { useList, useCreate, useUpdate, useDelete } =
 *     createLoanCrudHooks<SecurityItem, CreateInput, UpdateInput>('securities', 'securities');
 *
 *   // Simple read-only query
 *   const useOverview = createQueryHook<OverviewData>(['treasury', 'overview'], '/treasury/overview');
 */
import { type UseQueryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

// ── Simple query factory ────────────────────────────────

type QueryKeyParts = (string | number | Record<string, unknown> | undefined)[];

export function createQueryHook<TData>(
  keyParts: QueryKeyParts,
  path: string,
  defaultOptions?: Partial<UseQueryOptions<TData>>,
) {
  return function useGeneratedQuery(
    params?: Record<string, string | number | boolean | undefined>,
    options?: Partial<UseQueryOptions<TData>>,
  ) {
    const qs = params ? buildQueryString(params) : '';
    return useQuery<TData>({
      queryKey: [...keyParts, ...(params ? [params] : [])],
      queryFn: async () => {
        const { data } = await api.get<TData>(`${path}${qs}`);
        return data;
      },
      ...defaultOptions,
      ...options,
    });
  };
}

// ── Parameterised query (e.g. useDetail(id)) ────────────

export function createDetailHook<TData>(
  resource: string,
  basePath: string,
  defaultOptions?: Partial<UseQueryOptions<TData>>,
) {
  return function useDetail(id: string, options?: Partial<UseQueryOptions<TData>>) {
    return useQuery<TData>({
      queryKey: [resource, id],
      queryFn: async () => {
        const { data } = await api.get<TData>(`${basePath}/${id}`);
        return data;
      },
      enabled: !!id,
      ...defaultOptions,
      ...options,
    });
  };
}

// ── Global CRUD factory ─────────────────────────────────

export interface CrudHooks<T, TCreate = any, TUpdate = any> {
  useList: ReturnType<typeof createQueryHook<T[]>>;
  useDetail: ReturnType<typeof createDetailHook<T>>;
  useCreate: () => ReturnType<typeof useMutation<T, Error, TCreate>>;
  useUpdate: () => ReturnType<typeof useMutation<T, Error, TUpdate & { id: string }>>;
  useDelete: () => ReturnType<typeof useMutation<void, Error, string>>;
}

export function createCrudHooks<T, TCreate = any, TUpdate = any>(
  resource: string,
  basePath: string,
  opts?: { staleTime?: number },
) {
  const useList = createQueryHook<T[]>(
    [resource],
    basePath,
    opts?.staleTime ? { staleTime: opts.staleTime } : undefined,
  );

  const useDetail = createDetailHook<T>(resource, basePath);

  function useCreate() {
    const qc = useQueryClient();
    return useMutation<T, Error, TCreate>({
      mutationFn: async (payload: TCreate) => {
        const { data } = await api.post<T>(basePath, payload);
        return data;
      },
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: [resource] });
      },
    });
  }

  function useUpdate() {
    const qc = useQueryClient();
    return useMutation<T, Error, TUpdate & { id: string }>({
      mutationFn: async ({ id, ...payload }: TUpdate & { id: string }) => {
        const { data } = await api.patch<T>(`${basePath}/${id}`, payload);
        return data;
      },
      onSuccess: (_data, vars) => {
        qc.invalidateQueries({ queryKey: [resource] });
        qc.invalidateQueries({ queryKey: [resource, vars.id] });
      },
    });
  }

  function useDelete() {
    const qc = useQueryClient();
    return useMutation<void, Error, string>({
      mutationFn: async (id: string) => {
        await api.delete(`${basePath}/${id}`);
      },
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: [resource] });
      },
    });
  }

  return { useList, useDetail, useCreate, useUpdate, useDelete };
}

// ── Loan-scoped CRUD factory ────────────────────────────

export function createLoanCrudHooks<T, TCreate = any, TUpdate = any>(
  resource: string,
  subPath: string,
) {
  function useList(loanId: string) {
    return useQuery<T[]>({
      queryKey: [resource, loanId],
      queryFn: async () => {
        const { data } = await api.get<T[]>(`/loans/${loanId}/${subPath}`);
        return data;
      },
      enabled: !!loanId,
    });
  }

  function useCreate(loanId: string) {
    const qc = useQueryClient();
    return useMutation<T, Error, TCreate>({
      mutationFn: async (payload: TCreate) => {
        const { data } = await api.post<T>(`/loans/${loanId}/${subPath}`, payload);
        return data;
      },
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: [resource, loanId] });
      },
    });
  }

  function useUpdate(loanId: string) {
    const qc = useQueryClient();
    return useMutation<T, Error, TUpdate & { id: string }>({
      mutationFn: async ({ id, ...payload }: TUpdate & { id: string }) => {
        const { data } = await api.patch<T>(`/loans/${loanId}/${subPath}/${id}`, payload);
        return data;
      },
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: [resource, loanId] });
      },
    });
  }

  function useDelete(loanId: string) {
    const qc = useQueryClient();
    return useMutation<void, Error, string>({
      mutationFn: async (id: string) => {
        await api.delete(`/loans/${loanId}/${subPath}/${id}`);
      },
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: [resource, loanId] });
      },
    });
  }

  return { useList, useCreate, useUpdate, useDelete };
}

// ── Mutation factory (standalone) ───────────────────────

export function createMutationHook<TData, TVars>(
  method: 'post' | 'patch' | 'put' | 'delete',
  pathFn: (vars: TVars) => string,
  invalidateKeys?: (vars: TVars) => string[][],
) {
  return function useGeneratedMutation() {
    const qc = useQueryClient();
    return useMutation<TData, Error, TVars>({
      mutationFn: async (vars: TVars) => {
        const path = pathFn(vars);
        if (method === 'delete') {
          await api.delete(path);
          return undefined as unknown as TData;
        }
        const { data } = await api[method]<TData>(path, vars);
        return data;
      },
      onSuccess: (_data, vars) => {
        if (invalidateKeys) {
          for (const key of invalidateKeys(vars)) {
            qc.invalidateQueries({ queryKey: key });
          }
        }
      },
    });
  };
}

// ── Helpers ─────────────────────────────────────────────

function buildQueryString(params: Record<string, string | number | boolean | undefined>): string {
  const sp = new URLSearchParams();
  for (const [key, val] of Object.entries(params)) {
    if (val !== undefined && val !== '') sp.set(key, String(val));
  }
  const str = sp.toString();
  return str ? `?${str}` : '';
}
